import React, { useState, useEffect } from 'react'
import axios from 'axios'
import toast from 'react-hot-toast'

interface BulkOperationsModalProps {
  isOpen: boolean
  onClose: () => void
  selectedTickets: string[]
  onSuccess: () => void
}

interface BulkOperationData {
  statusId?: string
  assignedTo?: string
  priorityId?: string
  tags?: string[]
}

const BulkOperationsModal: React.FC<BulkOperationsModalProps> = ({
  isOpen,
  onClose,
  selectedTickets,
  onSuccess
}) => {
  const [operation, setOperation] = useState<string>('')
  const [data, setData] = useState<BulkOperationData>({})
  const [isLoading, setIsLoading] = useState(false)
  const [statuses, setStatuses] = useState<any[]>([])
  const [priorities, setPriorities] = useState<any[]>([])
  const [users, setUsers] = useState<any[]>([])
  const [newTag, setNewTag] = useState('')

  useEffect(() => {
    if (isOpen) {
      fetchFormData()
    }
  }, [isOpen])

  const fetchFormData = async () => {
    try {
      const [statusesRes, prioritiesRes, usersRes] = await Promise.all([
        axios.get('/api/tickets/statuses'),
        axios.get('/api/tickets/priorities'),
        axios.get('/api/users/staff')
      ])

      if (statusesRes.data.success) setStatuses(statusesRes.data.data)
      if (prioritiesRes.data.success) setPriorities(prioritiesRes.data.data)
      if (usersRes.data.success) setUsers(usersRes.data.data)
    } catch (error) {
      console.error('Error fetching form data:', error)
      toast.error('Failed to load form data')
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!operation) {
      toast.error('Please select an operation')
      return
    }

    // Validate required data for each operation
    if (operation === 'update-status' && !data.statusId) {
      toast.error('Please select a status')
      return
    }
    if (operation === 'assign-to' && !data.assignedTo) {
      toast.error('Please select a user to assign to')
      return
    }
    if (operation === 'update-priority' && !data.priorityId) {
      toast.error('Please select a priority')
      return
    }
    if (operation === 'add-tags' && (!data.tags || data.tags.length === 0)) {
      toast.error('Please add at least one tag')
      return
    }

    setIsLoading(true)
    
    try {
      const response = await axios.post('/api/advanced-tickets/bulk-operations', {
        operation,
        ticketIds: selectedTickets,
        data
      })
      
      if (response.data.success) {
        toast.success(`Bulk ${operation.replace('-', ' ')} completed successfully!`)
        onSuccess()
        onClose()
        resetForm()
      } else {
        toast.error(response.data.error || 'Failed to perform bulk operation')
      }
    } catch (error: any) {
      console.error('Error performing bulk operation:', error)
      toast.error(error.response?.data?.error || 'Failed to perform bulk operation')
    } finally {
      setIsLoading(false)
    }
  }

  const resetForm = () => {
    setOperation('')
    setData({})
    setNewTag('')
  }

  const addTag = () => {
    if (newTag.trim() && (!data.tags || !data.tags.includes(newTag.trim()))) {
      setData(prev => ({
        ...prev,
        tags: [...(prev.tags || []), newTag.trim()]
      }))
      setNewTag('')
    }
  }

  const removeTag = (tagToRemove: string) => {
    setData(prev => ({
      ...prev,
      tags: prev.tags?.filter(tag => tag !== tagToRemove) || []
    }))
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            Bulk Operations ({selectedTickets.length} tickets)
          </h3>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Operation Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Operation *
              </label>
              <select
                value={operation}
                onChange={(e) => setOperation(e.target.value)}
                className="input-field w-full"
                required
              >
                <option value="">Select operation</option>
                <option value="update-status">Update Status</option>
                <option value="assign-to">Assign To</option>
                <option value="update-priority">Change Priority</option>
                <option value="add-tags">Add Tags</option>
              </select>
            </div>

            {/* Status Update */}
            {operation === 'update-status' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  New Status *
                </label>
                <select
                  value={data.statusId || ''}
                  onChange={(e) => setData(prev => ({ ...prev, statusId: e.target.value }))}
                  className="input-field w-full"
                  required
                >
                  <option value="">Select status</option>
                  {statuses.map(status => (
                    <option key={status.id} value={status.id}>
                      {status.name.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Assign To */}
            {operation === 'assign-to' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Assign To *
                </label>
                <select
                  value={data.assignedTo || ''}
                  onChange={(e) => setData(prev => ({ ...prev, assignedTo: e.target.value }))}
                  className="input-field w-full"
                  required
                >
                  <option value="">Select user</option>
                  {users.map(user => (
                    <option key={user.id} value={user.id}>
                      {user.firstName} {user.lastName} ({user.email})
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Priority Update */}
            {operation === 'update-priority' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  New Priority *
                </label>
                <select
                  value={data.priorityId || ''}
                  onChange={(e) => setData(prev => ({ ...prev, priorityId: e.target.value }))}
                  className="input-field w-full"
                  required
                >
                  <option value="">Select priority</option>
                  {priorities.map(priority => (
                    <option key={priority.id} value={priority.id}>
                      {priority.displayName || priority.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Add Tags */}
            {operation === 'add-tags' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Add Tags *
                </label>
                <div className="flex space-x-2">
                  <input
                    type="text"
                    value={newTag}
                    onChange={(e) => setNewTag(e.target.value)}
                    className="input-field flex-1"
                    placeholder="Enter tag"
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                  />
                  <button
                    type="button"
                    onClick={addTag}
                    className="btn-secondary"
                  >
                    Add
                  </button>
                </div>
                
                {data.tags && data.tags.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {data.tags.map((tag, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                      >
                        {tag}
                        <button
                          type="button"
                          onClick={() => removeTag(tag)}
                          className="ml-1 inline-flex items-center justify-center w-4 h-4 rounded-full text-blue-400 hover:text-blue-600 hover:bg-blue-200"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="btn-secondary"
                disabled={isLoading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn-primary"
                disabled={isLoading}
              >
                {isLoading ? 'Processing...' : 'Apply to All Tickets'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

export default BulkOperationsModal
